import { useCallback, useMemo, useRef, useState } from "react";
import {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
} from "@mui/material";
import { TObjectShareResponse } from "../../../../../../services/api/instances/storageApi/types/service_types";
import { AppTextField } from "../../../../../../shared/components/AppInput/AppTextField";
import { TPermissionAction, UsersAutocompleteConfig } from "../../types";
import { useCollapsedTags } from "./use-collapsed-tags";
import { UserAutocompleteTags } from "../UserAutocompleteTags";
import {
  filterDuplicateUsernames,
  getAllObjectUsernames,
  normalizeAutocompleteValue,
} from "../helpers";
import { useAppDispatch } from "../../../../../../services/redux/hooks/use-dispatch";
import { createNotification } from "../../../../../../app/state/notificationSlice/slice";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import { selectSharedObjects } from "../../../../../../app/state/storageObjectSlice/selectors";
import { AppTooltip } from "../../../../../../shared/components/AppTooltip";

export const useUsersAutocomplete = (
  permission: TObjectShareResponse,
  handleChangeFields: (
    data: string | string[] | Date | null,
    type: TPermissionAction,
    id: string,
  ) => void,
  handleRevokeAccess: (id: number) => void,
  handleDeleteBlock: (id: number) => void,
  config: UsersAutocompleteConfig = {},
) => {
  const dispatch = useAppDispatch();
  const sharedObject = useAppSelector(selectSharedObjects);
  const [currentInput, setCurrentInput] = useState("");
  const { placeholder = "Введите имя и нажмите 'Enter'", classNames = {} } =
    config;
  const pendingValueRef = useRef<string | null>(null);

  const value = useMemo(
    () =>
      Array.isArray(permission.username)
        ? permission.username
        : [permission.username],
    [permission.username],
  );

  const { isExpanded, visibleCount, toggle, collapse } =
    useCollapsedTags(value);

  const handleChange = useCallback(
    (_: unknown, newValue: unknown, reason: any) => {
      if (reason === "clear") {
        handleDeleteBlock(permission.id);
        return;
      }
      const normalized = normalizeAutocompleteValue(newValue as string[]);
      const prevUsernames = Array.isArray(permission?.username)
        ? permission.username
        : [permission?.username].filter(Boolean);

      const addedValues = normalized.filter(
        (name) => !prevUsernames.includes(name),
      );
      const isMultiPaste = addedValues.length > 1;

      const globalValues = getAllObjectUsernames(
        String(permission.object_id),
        sharedObject,
      );
      const unique: string[] = filterDuplicateUsernames(
        globalValues,
        normalized,
        Array.isArray(permission?.username)
          ? permission?.username
          : [permission?.username],
      );

      if (!isMultiPaste && pendingValueRef.current !== null) {
        pendingValueRef.current = null;
        return;
      }
      if (isMultiPaste) {
        if (unique.length < normalized.length) {
          dispatch(
            createNotification(
              "info",
              `В переданном списке найдены дубликаты (${normalized.length - unique.length}). Сохранены только уникальные значения`,
            ),
          );
        }
      }

      handleChangeFields(unique, "username", String(permission.id));
    },
    [
      permission.id,
      permission.username,
      permission.object_id,
      sharedObject,
      handleChangeFields,
      dispatch,
    ],
  );

  const renderTags = useCallback(
    (tags: string[], getTagProps: AutocompleteRenderGetTagProps) => {
      return (
        <UserAutocompleteTags
          tags={tags}
          getTagProps={getTagProps}
          visibleCount={visibleCount}
          isExpanded={isExpanded}
          handleRevokeAccess={handleRevokeAccess}
          toggle={toggle}
          permission={permission}
        />
      );
    },
    [isExpanded, visibleCount, toggle, permission, handleRevokeAccess],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        setCurrentInput("");
        const inputValue = (event.target as HTMLInputElement).value.trim();
        const globalValues = getAllObjectUsernames(
          String(permission.object_id),
          sharedObject,
        );
        const isDuplicate = globalValues.some(
          (v) =>
            v.toLowerCase().trim() ===
            inputValue.toLowerCase().replace("@education_tools", "").trim(),
        );

        if (isDuplicate) {
          pendingValueRef.current = inputValue;
          dispatch(
            createNotification(
              "warning",
              `Имя "${inputValue}" уже добавлено`,
              5000,
            ),
          );
        } else {
          pendingValueRef.current = null;
        }
      }
    },
    [permission.object_id, sharedObject, dispatch],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <AppTooltip
        title="Нажмите Enter чтобы добавить"
        open={currentInput.length > 0}
        offsets={[-130, -20]}
      >
        <AppTextField
          variant="outlined"
          placeholder={placeholder}
          className={classNames.input}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            collapse();
            setCurrentInput("");
          }}
          onChange={(e) => setCurrentInput(e.target.value)}
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: params.InputProps.endAdornment,
          }}
        />
      </AppTooltip>
    ),
    [currentInput],
  );

  return { value, handleChange, renderTags, renderInput };
};
